export type SetupItem = { id: string } & Record<string, unknown>;

export interface FieldOption {
  label: string;
  value: string;
}

export interface SetupField {
  defaultValue?: boolean | number | string;
  key: string;
  label: string;
  // Sits behind "More details"; every field still reaches the API.
  more?: boolean;
  options?: readonly FieldOption[];
  // Option values only an existing item may keep.
  legacyOptions?: readonly string[];
  // An existing value outside the options stays selectable as it was typed.
  keepCurrent?: boolean;
  placeholder?: string;
  required?: boolean;
  type?:
    "checkbox" | "date" | "email" | "number" | "select" | "text" | "textarea";
}

export interface SetupResource {
  addLabel: string;
  archiveLabel?: string;
  emptyLabel?: string;
  fields: readonly SetupField[];
  key: string;
  noun: string;
  path: string;
  title: string;
}

const option = (value: string, label: string): FieldOption => ({
  label,
  value,
});

export const areaResource: SetupResource = {
  addLabel: "Add area",
  fields: [
    {
      key: "name",
      label: "Name",
      required: true,
    },
    {
      key: "kind",
      label: "Type",
      options: [
        option("prep", "Preparation"),
        option("storage", "Storage"),
        option("cold_storage", "Cold storage"),
        option("wash", "Washing"),
        option("waste", "Waste"),
        option("service", "Service"),
        option("office", "Office"),
        option("exterior", "Exterior"),
        option("other", "Other"),
      ],
      required: true,
      type: "select",
    },
    { key: "notes", label: "Notes", more: true, type: "textarea" },
  ],
  key: "areas",
  noun: "area",
  path: "areas",
  title: "Areas",
};

export const assetResource: SetupResource = {
  addLabel: "Add fridge or equipment",
  archiveLabel: "Retire",
  emptyLabel: "No fridges yet",
  fields: [
    {
      key: "name",
      label: "Name",
      required: true,
    },
    {
      defaultValue: "fridge",
      key: "asset_type",
      label: "Type",
      required: true,
      type: "select",
      options: [
        option("fridge", "Fridge"),
        option("freezer", "Freezer"),
        option("chiller", "Chiller"),
        option("hot_hold", "Hot hold"),
        option("cook_equipment", "Cooking equipment"),
        option("dishwasher", "Dishwasher"),
        option("thermometer", "Thermometer"),
        option("probe", "Probe"),
        option("water_system", "Water system"),
        option("water_tank", "Water tank"),
        option("smoker", "Smoker"),
        option("water_bath", "Water bath"),
        option("dehydrator", "Dehydrator"),
        option("vehicle", "Vehicle"),
        option("other", "Other"),
      ],
    },
    { key: "area_id", label: "Area", type: "select" },
    { key: "code", label: "Code", more: true },
    {
      defaultValue: "normal",
      key: "criticality",
      label: "Importance",
      more: true,
      type: "select",
      options: [option("normal", "Normal"), option("critical", "Critical")],
    },
    { key: "target_min_c", label: "Lowest °C", more: true, type: "number" },
    { key: "target_max_c", label: "Highest °C", more: true, type: "number" },
    {
      key: "calibration_interval_days",
      label: "Calibrate every (days)",
      more: true,
      type: "number",
    },
    {
      key: "last_calibrated_on",
      label: "Last calibrated",
      more: true,
      type: "date",
    },
    { key: "notes", label: "Notes", more: true, type: "textarea" },
  ],
  key: "assets",
  noun: "fridge or equipment item",
  path: "assets",
  title: "Fridges & equipment",
};

export const supplierResource: SetupResource = {
  addLabel: "Add supplier",
  fields: [
    {
      key: "name",
      label: "Name",
      required: true,
    },
    {
      key: "goods_summary",
      label: "Supplies",
      type: "textarea",
    },
    {
      key: "site_registration_no",
      label: "Registration number",
      more: true,
    },
    {
      key: "registration_expires_on",
      label: "Registration expires",
      more: true,
      type: "date",
    },
    { key: "contact_person", label: "Contact", more: true },
    { key: "phone", label: "Phone", more: true },
    { key: "email", label: "Email", more: true, type: "email" },
    { key: "notes", label: "Notes", more: true, type: "textarea" },
  ],
  key: "suppliers",
  noun: "supplier",
  path: "suppliers",
  title: "Suppliers",
};

export const cleaningResource: SetupResource = {
  addLabel: "Add cleaning task",
  fields: [
    {
      key: "target",
      label: "What",
      required: true,
    },
    {
      defaultValue: "daily",
      keepCurrent: true,
      key: "frequency",
      label: "How often",
      options: [
        option("daily", "Daily"),
        option("weekly", "Weekly"),
        option("monthly", "Monthly"),
        option("after each use", "After each use"),
      ],
      required: true,
      type: "select",
    },
    {
      key: "method",
      label: "How",
      required: true,
      type: "textarea",
    },
    {
      defaultValue: true,
      key: "task_logging_enabled",
      label: "Add to the kitchen’s checklist",
      type: "checkbox",
    },
    { key: "chemical", label: "Product", more: true },
    { key: "responsibility", label: "Who", more: true },
    {
      key: "safety_notes",
      label: "Safety notes",
      more: true,
      type: "textarea",
    },
  ],
  key: "cleaning",
  noun: "cleaning task",
  path: "cleaning-plan-items",
  title: "Cleaning",
};

export const waterResource: SetupResource = {
  addLabel: "Add water source",
  fields: [
    {
      key: "kind",
      label: "Source",
      required: true,
      type: "select",
      options: [
        option("registered_supply", "Registered supply"),
        option("roof", "Roof water"),
        option("bore", "Bore"),
        option("surface", "Surface water"),
        option("tanker", "Tanker"),
        option("other", "Other"),
      ],
    },
    { key: "supplier_name", label: "Supplier" },
    { key: "treatment", label: "Treatment", type: "textarea" },
    {
      defaultValue: "ok",
      key: "safe_status",
      label: "Status",
      type: "select",
      options: [
        option("ok", "OK"),
        option("attention", "Needs attention"),
        option("unsafe", "Problem"),
      ],
    },
    {
      key: "description",
      label: "Notes",
      more: true,
      type: "textarea",
    },
  ],
  key: "water",
  noun: "water source",
  path: "water-sources",
  title: "Water",
};

export const riskResource: SetupResource = {
  addLabel: "Add nearby risk",
  fields: [
    {
      key: "hazard",
      label: "Risk",
      required: true,
      type: "textarea",
    },
    { key: "control", label: "What you do", required: true, type: "textarea" },
    { key: "source", label: "Source", more: true },
    {
      defaultValue: "active",
      key: "status",
      label: "Status",
      more: true,
      type: "select",
      options: [option("active", "Active"), option("resolved", "Resolved")],
    },
  ],
  key: "risks",
  noun: "nearby risk",
  path: "nearby-risks",
  title: "Nearby risks",
};

export const separationResource: SetupResource = {
  addLabel: "Add separation",
  fields: [
    {
      key: "control_type",
      label: "Type",
      required: true,
      type: "select",
      options: [
        option("raw_cooked", "Raw and cooked"),
        option("allergen", "Allergen"),
        option("chemical", "Chemical"),
        option("foreign_matter", "Foreign matter"),
        option("other", "Other"),
      ],
    },
    { key: "hazard", label: "Risk", required: true, type: "textarea" },
    {
      key: "method",
      label: "How you keep it apart",
      required: true,
      type: "textarea",
    },
    { key: "areas_note", label: "Where", more: true, type: "textarea" },
    { key: "review_due", label: "Review by", more: true, type: "date" },
  ],
  key: "separation",
  noun: "separation",
  path: "separation-controls",
  title: "Separation",
};

export const methodResource: SetupResource = {
  addLabel: "Add process",
  fields: [
    { key: "name", label: "Name", required: true },
    {
      key: "record_type_code",
      label: "Process",
      required: true,
      type: "select",
      options: [
        option("cook", "Cooking"),
        option("reheat", "Reheating"),
        option("cooling", "Cooling"),
        option("water_activity", "Water activity"),
        option("acidification", "Acidification"),
        option("hot_smoke", "Hot smoking"),
        option("sous_vide", "Sous vide"),
        option("duck_drying", "Duck drying"),
      ],
    },
    { key: "food_description", label: "Food", type: "textarea" },
    { key: "method_text", label: "Method", more: true, type: "textarea" },
    { key: "equipment_text", label: "Equipment", more: true, type: "textarea" },
    {
      defaultValue: true,
      key: "weekly_check_due",
      label: "Check every week",
      more: true,
      type: "checkbox",
    },
  ],
  key: "methods",
  noun: "process",
  path: "process-methods",
  title: "Special processes",
};

export const documentResource: SetupResource = {
  addLabel: "Add document",
  fields: [
    { key: "title", label: "Title", required: true },
    {
      key: "doc_type",
      label: "Type",
      required: true,
      type: "select",
      options: [
        option("site_layout", "Site layout"),
        option("site_map", "Site map"),
        option("manager_certificate", "Manager certificate"),
        option("lcq", "LCQ"),
        option("alcohol_licence", "Alcohol licence"),
        option("pest_contract", "Pest contract"),
        option("pest_report", "Pest report"),
        option("water_test_certificate", "Water test certificate"),
        option("equipment_service", "Equipment service"),
        option("registration_certificate", "Registration certificate"),
        option("verification_report", "Verification report"),
        option("supplier_evidence", "Supplier evidence"),
        option("other", "Other"),
      ],
    },
    { key: "expires_on", label: "Expires on", type: "date" },
    {
      defaultValue: true,
      key: "create_reminder",
      label: "Remind me before it expires",
      type: "checkbox",
    },
    { key: "reference_no", label: "Reference number", more: true },
    { key: "issuer", label: "Issuer", more: true },
    { key: "issued_on", label: "Issued on", more: true, type: "date" },
    { key: "notes", label: "Notes", more: true, type: "textarea" },
    {
      defaultValue: false,
      key: "confidential",
      label: "Confidential",
      more: true,
      type: "checkbox",
    },
  ],
  key: "documents",
  noun: "document",
  path: "documents",
  title: "Documents",
};

// Document expiry reminders come from the document itself; this list holds
// the other renewals.
export const reminderResource: SetupResource = {
  addLabel: "Add renewal",
  archiveLabel: "Dismiss",
  fields: [
    { key: "title", label: "Title", required: true },
    {
      key: "subject_kind",
      label: "Type",
      required: true,
      type: "select",
      legacyOptions: ["document_expiry"],
      options: [
        option("fcp_registration", "Food control plan registration"),
        option("verification", "Verification"),
        option("manager_certificate", "Manager certificate"),
        option("lcq", "LCQ"),
        option("alcohol_licence", "Alcohol licence"),
        option("pest_service", "Pest service"),
        option("equipment_service", "Equipment service"),
        option("thermometer_calibration", "Thermometer calibration"),
        option("document_expiry", "Document expiry"),
        option("custom", "Other"),
      ],
    },
    { key: "due_on", label: "Due on", required: true, type: "date" },
    { key: "notes", label: "Notes", more: true, type: "textarea" },
  ],
  key: "reminders",
  noun: "renewal",
  path: "renewal-reminders",
  title: "Renewals",
};

export const setupResources = [
  areaResource,
  assetResource,
  supplierResource,
  cleaningResource,
  waterResource,
  riskResource,
  separationResource,
  methodResource,
  documentResource,
  reminderResource,
] as const;

// The API only accepts a reminder when the document has an expiry date.
export function resourceValues(
  resource: SetupResource,
  values: Record<string, boolean | number | string | null>,
): Record<string, boolean | number | string | null> {
  if (resource.key !== "documents" || values.expires_on) return values;
  return { ...values, create_reminder: false };
}

export function labelFor(value: unknown): string {
  if (typeof value !== "string") return "";
  return value
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function itemTitle(resource: SetupResource, item: SetupItem): string {
  for (const key of [
    "name",
    "title",
    "target",
    "hazard",
    "supplier_name",
    "kind",
  ]) {
    const value = item[key];
    if (typeof value === "string" && value.length > 0) return value;
  }
  return labelFor(resource.noun);
}

export function itemSummary(resource: SetupResource, item: SetupItem): string {
  const parts = resource.fields.flatMap((field) => {
    if (
      ["name", "title", "target", "hazard", "notes"].includes(field.key) ||
      field.key.endsWith("_id") ||
      field.type === "checkbox"
    ) {
      return [];
    }
    const value = item[field.key];
    if (field.type === "select" && value === field.defaultValue) return [];
    if (typeof value === "string" && value.length > 0) {
      const match = field.options?.find((entry) => entry.value === value);
      return [match?.label ?? labelFor(value)];
    }
    if (typeof value === "number") return [String(value)];
    return [];
  });
  return parts.slice(0, 3).join(" · ");
}

export function visibleOptions(
  field: SetupField,
  current: unknown,
): readonly FieldOption[] {
  const options = (field.options ?? []).filter(
    (entry) =>
      !field.legacyOptions?.includes(entry.value) || entry.value === current,
  );
  if (
    field.keepCurrent &&
    typeof current === "string" &&
    current !== "" &&
    !options.some((entry) => entry.value === current)
  ) {
    return [...options, { label: current, value: current }];
  }
  return options;
}
